import { useState, useEffect, useRef } from 'react';
import Hero from './components/Hero';
import Marquee from './components/Marquee';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import Contact from './components/Contact';
import Footer from './components/Footer';

const READY_URL = 'https://ready.intevolabs.com';
const PRIME_URL = 'https://prime.intevolabs.com';

function ServiciosDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-[#8887A0] hover:text-[#F0EFF8] transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer flex items-center gap-1.5"
        style={{ fontFamily: 'Sora, sans-serif', fontSize: '14px' }}
      >
        Servicios
        <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-[240px] py-2 z-50"
          style={{
            background: '#0F0F1C',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px',
            boxShadow: '0 20px 40px -12px rgba(0,0,0,0.6)',
          }}
        >
          <a
            href={READY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-0.5 px-4 py-3 hover:bg-white/[0.04] transition-colors duration-150 cursor-pointer"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            <span className="text-[#F0EFF8] text-[14px] font-semibold">Intevo Ready</span>
            <span className="text-[#8887A0] text-[12.5px]">Planes fijos, desde $89/mes</span>
          </a>
          <a
            href={PRIME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col gap-0.5 px-4 py-3 hover:bg-white/[0.04] transition-colors duration-150 cursor-pointer"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            <span className="text-[#F0EFF8] text-[14px] font-semibold">Intevo Prime</span>
            <span className="text-[#8887A0] text-[12.5px]">A la medida, cotización según alcance</span>
          </a>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#080810] text-[#F0EFF8]" style={{ fontFamily: 'Sora, sans-serif' }}>
      {/* Noise texture overlay */}
      <div className="fixed inset-0 pointer-events-none z-[100] opacity-[0.03]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat',
      }} />

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#080810]/90 backdrop-blur-xl border-b border-white/[0.06]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/601b8a805a2f0f27f21e1ce6d1f2a810/80ae8f6d2d425f94fe707693601f876c.png"
              alt="Intevo Labs"
              className="h-9 w-auto"
            />
          </div>

          {/* Center Links */}
          <div className="flex items-center gap-10">
            <ServiciosDropdown />
            <button
              onClick={() => scrollToSection('process')}
              className="text-[#8887A0] hover:text-[#F0EFF8] transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif', fontSize: '14px' }}
            >
              Proceso
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-[#8887A0] hover:text-[#F0EFF8] transition-colors duration-200 font-medium whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Sora, sans-serif', fontSize: '14px' }}
            >
              Contacto
            </button>
          </div>

          {/* CTA Button */}
          <button
            onClick={() => scrollToSection('contact')}
            className="px-6 py-2.5 bg-[#7B3FE4] text-white hover:bg-[#6B2FD4] transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              borderRadius: '50px',
              boxShadow: '0 0 24px rgba(123,63,228,0.6)'
            }}
          >
            Comenzar
          </button>
        </div>
      </nav>

      <Hero onCTAClick={() => scrollToSection('contact')} />
      <Marquee />
      <Features />
      <HowItWorks />
      <Contact />
      <Footer onNavigate={scrollToSection} />
    </div>
  );
}
